import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type TouchEvent,
} from 'react';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in the sheet header and used as the dialog's accessible name. */
  title: string;
  /** Pinned action row at the bottom; already carries the safe-area inset padding. */
  footer?: ReactNode;
  /** Extra control rendered in the header, left of the × (e.g. a "Reset" button). */
  headerAction?: ReactNode;
  /** Allow dragging the sheet down to dismiss. Default true. */
  dragToDismiss?: boolean;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Past this much downward drag, releasing dismisses the sheet instead of snapping back. */
const DISMISS_DISTANCE = 110;

/**
 * The slide transform above is an inline style, so Tailwind's `motion-safe:` variant can't
 * gate it — check the media query directly. Not reactive to a live OS-setting flip, which
 * is fine: the check only needs to be right at the moment the sheet opens or closes.
 */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Slide-up modal sheet — the mobile counterpart to `<Modal>`.
 *
 * Handles the things a sheet has to get right on a phone: Escape and backdrop tap to
 * close, a focus trap while open with focus restored to the trigger on close, a body
 * scroll lock so the page behind doesn't rubber-band, `max-h-[90dvh]` with the body as
 * the only scroll region, safe-area padding under the footer, and optional
 * drag-down-to-dismiss.
 *
 * Renders nothing when `open` is false, so mount it unconditionally.
 *
 * @example
 * <BottomSheet open={open} onClose={close} title="Filters" footer={<button>Apply</button>}>
 *   …form…
 * </BottomSheet>
 */
export function BottomSheet({
  open,
  onClose,
  title,
  footer,
  headerAction,
  dragToDismiss = true,
  children,
}: BottomSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Slide-in: mount at translateY(100%), then flip on the next frame so the transition runs.
  const [entered, setEntered] = useState(false);
  // Live finger offset during a drag-to-dismiss, in px. 0 when not dragging.
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll lock. Restores whatever the page had before, not a hard-coded ''.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus in on open, back to the trigger on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      setEntered(true);
      closeRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      setEntered(false);
      setDragY(0);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  // Focus trap: keep Tab cycling inside the sheet.
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const nodes = sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!nodes || nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  function onGrabStart(e: TouchEvent) {
    if (!dragToDismiss || e.touches.length !== 1) return;
    dragStartY.current = e.touches[0].clientY;
  }

  function onGrabMove(e: TouchEvent) {
    if (dragStartY.current === null) return;
    // Only downward travel moves the sheet; dragging up does nothing.
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  }

  function onGrabEnd() {
    if (dragStartY.current === null) return;
    dragStartY.current = null;
    if (dragY > DISMISS_DISTANCE) onClose();
    else setDragY(0);
  }

  if (!open) return null;

  const dragging = dragStartY.current !== null;
  const translate = entered ? `${dragY}px` : '100%';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" onKeyDown={onKeyDown}>
      <div
        className={`absolute inset-0 bg-slate-900/40 motion-safe:transition-opacity motion-safe:duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative bg-white rounded-t-2xl shadow-xl w-full max-h-[90dvh] flex flex-col"
        style={{
          transform: `translateY(${translate})`,
          // Inline style, so a Tailwind `motion-safe:` variant can't reach it — check the
          // media query directly instead.
          transition: dragging || prefersReducedMotion() ? 'none' : 'transform 200ms ease-out',
        }}
      >
        <div
          className="pt-2 pb-1 flex justify-center touch-none"
          onTouchStart={onGrabStart}
          onTouchMove={onGrabMove}
          onTouchEnd={onGrabEnd}
          onTouchCancel={onGrabEnd}
          aria-hidden="true"
        >
          <span className="h-1 w-10 rounded-full bg-slate-300" />
        </div>

        <header
          className="px-4 pb-3 flex items-center gap-3 border-b border-slate-200"
          onTouchStart={onGrabStart}
          onTouchMove={onGrabMove}
          onTouchEnd={onGrabEnd}
          onTouchCancel={onGrabEnd}
        >
          <h2 id={titleId} className="text-lg font-semibold text-brand-charcoal truncate">
            {title}
          </h2>
          <div className="ml-auto flex items-center gap-1">
            {headerAction}
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-brand-charcoal text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 text-sm text-brand-charcoal">
          {children}
        </div>

        {footer ? (
          <footer className="px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-slate-200">
            {footer}
          </footer>
        ) : (
          <div className="pb-[env(safe-area-inset-bottom)]" />
        )}
      </div>
    </div>
  );
}
